import { GoogleMap, Marker, Polyline } from '@react-google-maps/api'
import React, { forwardRef, memo, useEffect, useRef, useState } from 'react'
import { TFinishLine } from '../../shared/types/finishLine.type';
import { TCheckPoint } from '../../shared/types/checkPoint.type';
import { LatLng } from 'use-places-autocomplete';
import { isEmpty } from 'lodash';
import { useMap } from '../../hooks/useMap';
import { MapRefProps } from '../../pages/user/TrackView';

const mapContainerStyle = {
    height: '450px',
    width: '100%',
    borderRadius: 4,
};

type FlCpMapProps = {
    adressCenter?: LatLng,
    finishLine: Partial<TFinishLine> | null,
    checkPoints?: TCheckPoint[],
    onFinishLinePosChanged: (values: google.maps.LatLngLiteral[]) => void
    onCheckPointPosChanged: (values: google.maps.LatLngLiteral[], id: number) => void

}

interface MarkerPosition {
    id: string;
    lat: number;
    lng: number;
    rawId?: number
}

interface MarkerPosCp extends MarkerPosition {
    label?: string
}

export interface PolylinePosition {
    lat: number;
    lng: number;
}


export const FlCpMap = forwardRef<MapRefProps, FlCpMapProps>(({ adressCenter, finishLine, checkPoints, onFinishLinePosChanged, onCheckPointPosChanged }, ref) => {


    const lineSymbol = {
        path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
    };

    const [zoomLevel, setZoomLevel] = useState(16); // Default zoom level
    const mapRef = useRef<google.maps.Map | null>(null); // Reference to the map instance
    const { addDirectionLine } = useMap()
    //Markers Arr
    const [markerFl, setMarkerFl] = useState<MarkerPosition[]>([]);
    const [polylineFl, setPolylineFl] = useState<PolylinePosition[]>([]);

    //Markers Checkpoints
    const [markerCp, setMarkerCp] = useState<MarkerPosCp[]>([]);
    const [polylinesCp, setPolylinesCp] = useState<PolylinePosition[][]>([]);
    const [dirCp, setDirCp] = useState<PolylinePosition[][]>([]);
    // Function to handle zoom changes


    React.useImperativeHandle(ref, () => ({
        getCenter() {
            if (mapRef.current) {
                const newCenter = mapRef.current.getCenter();
                if (newCenter) {
                    return { lat: newCenter.lat(), lng: newCenter.lng() };
                }
            }
        },
        async getAdressByPos(lat, lng) {
            let response = await relocateAdress(lat, lng)
            return response
        },
    }));

    const handleZoomChanged = () => {
        if (mapRef.current) {
            const newZoom = mapRef.current.getZoom(); // Get the current zoom level
            setZoomLevel(newZoom || 16);
        }
    };

    const onMapLoad = (map: google.maps.Map) => {
        mapRef.current = map; // Save map instance to the ref
        setZoomLevel(map.getZoom() || 16); // Set initial zoom level
    };


    useEffect(() => {
        if (finishLine) {
            let marker1: MarkerPosition = { id: 'fl1', lat: finishLine.latitude_a ?? 0, lng: finishLine.longitude_a ?? 0 }
            let marker2: MarkerPosition = { id: 'fl2', lat: finishLine.latitude_b ?? 0, lng: finishLine.longitude_b ?? 0 }
            let markers = [marker1, marker2]
            const polylinePath = markers.map((marker) => ({ lat: marker.lat, lng: marker.lng }));

            //Update State values
            setMarkerFl(markers)
            setPolylineFl(polylinePath)
        } else {
            //Update State values
            setMarkerFl([])
            setPolylineFl([])
        }
    }, [finishLine])

    useEffect(() => {
        if (!isEmpty(checkPoints)) {

            let Cps: MarkerPosCp[] = []
            let CpsPoly: PolylinePosition[][] = []
            let dirPolyline: PolylinePosition[][] = []

            checkPoints?.forEach(cp => {
                let marker1: MarkerPosCp = { id: `${cp.id}`, lat: cp.latitude_a ?? 0, lng: cp.longitude_a ?? 0, label: cp.name, rawId: cp.id }
                let marker2: MarkerPosCp = { id: `${cp.id}_2`, lat: cp.latitude_b ?? 0, lng: cp.longitude_b ?? 0, label: cp.name, rawId: cp.id }
                let markers = [marker1, marker2]

                const polylinePath = markers.map((marker) => ({ lat: marker.lat, lng: marker.lng }));

                //Direction line for checkpoints
                if (cp.direction !== 0) {
                    let polyDir1 = addDirectionLine(markers, cp.direction)
                    dirPolyline.push(polyDir1)
                } else {
                    let polyDir2 = addDirectionLine(markers, 1)
                    let polyDir3 = addDirectionLine(markers, -1)
                    dirPolyline.push(polyDir2)
                    dirPolyline.push(polyDir3)
                }

                Cps.push(...markers)
                CpsPoly.push(polylinePath)

            })


            //Update State values
            setMarkerCp(Cps)
            setPolylinesCp(CpsPoly)
            setDirCp(dirPolyline)
        } else {
            setMarkerCp([])
            setPolylinesCp([])
            setDirCp([])
        }
    }, [checkPoints])

    useEffect(() => {
        fitBounds()
    }, [markerFl.length, markerCp.length, adressCenter])

    //Handle Fl Change
    const handleMarkerFlDragEnd = (event: google.maps.MapMouseEvent, markerId: string) => {
        if (event.latLng) {
            const newLat = event.latLng.lat();
            const newLng = event.latLng.lng();

            //Filter for id
            let not_modified_marker = markerFl.find(marker => marker.id !== markerId)

            if (!not_modified_marker) return

            onFinishLinePosChanged([{ lat: newLat, lng: newLng }, { lat: not_modified_marker.lat, lng: not_modified_marker.lng }])


        }
    };

    //Handle Fl Change
    const handleMarkerCpDragEnd = (event: google.maps.MapMouseEvent, markerId: string) => {
        if (event.latLng) {
            const newLat = event.latLng.lat();
            const newLng = event.latLng.lng();

            let moved_cp = markerCp.find(cp => cp.id === markerId)

            if (!moved_cp) return

            let not_moved_cp = markerCp.find(cp => cp.id !== markerId && cp.rawId === moved_cp?.rawId)
            if (!not_moved_cp || !moved_cp?.rawId) return

            onCheckPointPosChanged([{ lat: newLat, lng: newLng }, { lat: not_moved_cp.lat, lng: not_moved_cp.lng }], moved_cp.rawId)




        }
    };
    const relocateAdress = async (lat?: number, lng?: number) => {
        if (!lat || !lng) return null
        try {
            const geocoder = new window.google.maps.Geocoder();
            let response = await geocoder.geocode({ location: { lat, lng } })
            if (response && !isEmpty(response.results)) {
                return response.results[0].formatted_address
            }
            return null
        } catch (error) {
            return null
        }
    }

    //Fit map on markers
    // Adjust bounds to include all markers
    const fitBounds = () => {
        const markers = [...markerFl, ...markerCp]

        const bounds = new google.maps.LatLngBounds();
        if (mapRef.current && (markers.length > 0 || adressCenter)) {

            if (!isEmpty(markers)) {
                markers.forEach(({ lat, lng }) => {
                    bounds.extend({ lat, lng });
                });
            } else {
                if (adressCenter) {
                    bounds.extend({ lat: adressCenter.lat, lng: adressCenter.lng });
                }
            }



            mapRef.current.fitBounds(bounds); // Fit map to bounds
        }
    };

    return (
        <GoogleMap
            mapContainerStyle={mapContainerStyle}
            zoom={zoomLevel}
            center={adressCenter}
            onZoomChanged={() => handleZoomChanged()}

            onLoad={onMapLoad} // Set initial zoom
            options={{
                mapTypeId: google.maps.MapTypeId.SATELLITE,
                mapTypeControl: false,
                zoomControl: true,
                streetViewControl: false,
                scrollwheel: true,
                disableDefaultUI: true,
                keyboardShortcuts: true,
                mapTypeControlOptions: {
                    style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR
                },
            }}
        >
            {/* FinishLine Section */}
            {markerFl.map((marker) => (
                <Marker
                    key={marker.id}
                    icon={require('../../assets/finish-line-marker.png')}
                    position={{ lat: marker.lat, lng: marker.lng }}
                    draggable={true} // Enable dragging for markers
                    onDragEnd={(event) => handleMarkerFlDragEnd(event, marker.id)} // Update marker position
                />
            ))}
            <Polyline
                path={polylineFl} // Connect marker positions
                options={{
                    strokeColor: "#FF0000", // Line color
                    strokeOpacity: 0.8, // Line opacity
                    strokeWeight: 2, // Line thickness
                }}
            />

            {/* FinishLine Section */}
            {markerCp.map((marker) => (
                <Marker
                    key={marker.id}
                    label={{ text: String(marker.label), color: 'white', fontSize: '10px' }}
                    position={{ lat: marker.lat, lng: marker.lng }}
                    draggable={true} // Enable dragging for markers
                    onDragEnd={(event) => handleMarkerCpDragEnd(event, marker.id)} // Update marker position
                />
            ))}

            {polylinesCp.map((poly, i) => (
                <Polyline
                    key={`key-cp-${i}`}
                    path={poly} // Connect marker positions
                    options={{
                        strokeColor: "#FF0000", // Line color
                        strokeOpacity: 0.8, // Line opacity
                        strokeWeight: 2, // Line thickness
                    }}
                />
            ))}

            {/* Direction Polylines for checkpoints */}
            {dirCp.map((poly, i) => (
                <Polyline
                    key={`key-cp-dir-${i}`}
                    path={poly} // Connect marker positions
                    options={{
                        icons: [
                            {
                                icon: lineSymbol,
                                offset: "100%",
                            }
                        ]
                    }}
                />
            ))}

        </GoogleMap>
    )
})
